import { BackgroundStyle, LetterCase, SubtitleStyles } from "../../types";

export interface CaptionPreset {
  id: string;
  name: string;
  tagline: string;
  styles: SubtitleStyles;
}

// Applied before a preset so switching presets never leaks look-related
// settings from the previous one. Position and progress bar are kept.
export const PRESET_BASE: SubtitleStyles = {
  background_style: BackgroundStyle.NONE,
  background_color: "#000000",
  outline_thickness: 0,
  outline_color: "#000000",
  bold: false,
  italic: false,
  underline: false,
  strikethrough: false,
  letter_case: LetterCase.DEFAULT,
  letter_spacing: 0,
  line_height: 1.2,
  caption_animation: "none",
  highlight_color: "#FACC15",
  highlight_text_color: "#FFFFFF",
  max_words_per_caption: 0,
};

// Defaults for fields added after launch, so undo can clear them reliably
// (the API merges styles shallowly and would otherwise keep stale values).
export const ANIMATION_FIELD_DEFAULTS: SubtitleStyles = {
  preset_id: "",
  caption_animation: "none",
  highlight_color: "#FACC15",
  highlight_text_color: "#FFFFFF",
  max_words_per_caption: 0,
};

export const CAPTION_PRESETS: CaptionPreset[] = [
  {
    id: "bold-pop",
    name: "Bold Pop",
    tagline: "Punchy, word pops in yellow",
    styles: {
      font: "Montserrat",
      size: 32,
      color: "#FFFFFF",
      bold: true,
      letter_case: LetterCase.UPPERCASE,
      outline_thickness: 3,
      outline_color: "#000000",
      caption_animation: "pop",
      highlight_color: "#FACC15",
      max_words_per_caption: 3,
    },
  },
  {
    id: "beast",
    name: "Beast",
    tagline: "Tall, loud, green highlight",
    styles: {
      font: "Bebas Neue",
      size: 42,
      color: "#FFFFFF",
      letter_case: LetterCase.UPPERCASE,
      letter_spacing: 1,
      outline_thickness: 3,
      outline_color: "#000000",
      caption_animation: "highlight",
      highlight_color: "#22C55E",
      max_words_per_caption: 2,
    },
  },
  {
    id: "karaoke",
    name: "Karaoke",
    tagline: "Words light up as they're said",
    styles: {
      font: "Outfit",
      size: 30,
      color: "#FFFFFF",
      bold: true,
      outline_thickness: 2,
      outline_color: "#000000",
      caption_animation: "karaoke",
      highlight_color: "#38BDF8",
      max_words_per_caption: 5,
    },
  },
  {
    id: "box",
    name: "Box",
    tagline: "Active word on a color block",
    styles: {
      font: "Inter",
      size: 28,
      color: "#FFFFFF",
      bold: true,
      outline_thickness: 2,
      outline_color: "#000000",
      caption_animation: "box",
      highlight_color: "#7C3AED",
      highlight_text_color: "#FFFFFF",
      max_words_per_caption: 4,
    },
  },
  {
    id: "one-word",
    name: "One Word",
    tagline: "Single word, big and fast",
    styles: {
      font: "Montserrat",
      size: 46,
      color: "#FFFFFF",
      bold: true,
      letter_case: LetterCase.UPPERCASE,
      outline_thickness: 4,
      outline_color: "#000000",
      caption_animation: "single_word",
      highlight_color: "#FFFFFF",
      max_words_per_caption: 1,
    },
  },
  {
    id: "neon",
    name: "Neon",
    tagline: "Pink glow edges",
    styles: {
      font: "Oswald",
      size: 34,
      color: "#FFFFFF",
      bold: true,
      letter_case: LetterCase.UPPERCASE,
      outline_thickness: 3,
      outline_color: "#DB2777",
      caption_animation: "highlight",
      highlight_color: "#F9A8D4",
      max_words_per_caption: 3,
    },
  },
  {
    id: "classic",
    name: "Classic",
    tagline: "Subtitle bar, easy to read",
    styles: {
      font: "Inter",
      size: 24,
      color: "#FFFFFF",
      background_style: BackgroundStyle.ROUNDED,
      background_color: "#000000",
    },
  },
  {
    id: "editorial",
    name: "Editorial",
    tagline: "Elegant serif italics",
    styles: {
      font: "Playfair Display",
      size: 30,
      color: "#FFFFFF",
      italic: true,
      outline_thickness: 1,
      outline_color: "#000000",
      caption_animation: "karaoke",
      highlight_color: "#FDE68A",
    },
  },
];

export const applyPreset = (
  current: SubtitleStyles,
  preset: CaptionPreset,
): SubtitleStyles => ({
  ...current,
  ...PRESET_BASE,
  ...preset.styles,
  preset_id: preset.id,
});
